import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, 
  Mail, 
  Phone, 
  Award, 
  Globe, 
  Users,
  Calendar,
  ArrowRight
} from 'lucide-react';

const Team = () => {
  const { t } = useTranslation('public');
  const teamMembers = [
    {
      name: 'Arnaud',
      role: 'Directeur - Activité France',
      image: '/api/placeholder/300/300',
      specialties: [
        'Assurances particuliers',
        'Assurances professionnels', 
        'Santé collective',
        'Prévoyance entreprise'
      ],
      experience: '10+ années d\'expérience',
      description: 'Arnaud dirige l\'activité France d\'ASAR-CSG. Expert en assurances particuliers et professionnels, il accompagne ses clients dans tous leurs projets avec une approche personnalisée et des solutions sur mesure.',
      contact: {
        email: 'arnaud@asar-csg.com',
        phone: '+33 1 84 80 87 29'
      },
      expertise: [
        'Analyse des risques particuliers et professionnels',
        'Optimisation des couvertures existantes',
        'Négociation avec les compagnies d\'assurance',
        'Conseil en protection sociale'
      ]
    },
    {
      name: 'Sokha',
      role: 'Directrice - Activité International',
      image: '/api/placeholder/300/300',
      specialties: [
        'Assurances expatriés',
        'Couverture internationale',
        'Assurances voyage',
        'Rapatriement sanitaire'
      ],
      experience: '8+ années d\'expérience internationale',
      description: 'Sokha pilote l\'activité internationale d\'ASAR-CSG. Spécialiste des assurances expatriés et de la couverture internationale, elle accompagne les clients dans leurs projets de mobilité à travers le monde.',
      contact: {
        email: 'sokha@asar-csg.com',
        phone: '+33 1 84 80 87 29'
      },
      expertise: [
        'Couverture santé internationale',
        'Réglementations internationales',
        'Assistance expatriés',
        'Solutions de mobilité'
      ]
    }
  ];

  const companyStats = [
    { number: '2014', label: t('team.stats.creation') },
    { number: '1000+', label: t('team.stats.clients') },
    { number: '4', label: t('team.stats.expertise') },
    { number: '50+', label: t('team.stats.countries') }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-primary-mid to-brand-text-light text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('team.title')}
            </h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-100">
              {t('team.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {companyStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-brand-primary-mid mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('team.meetExperts')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('team.meetExpertsSubtitle')}
            </p>
          </div>

          <div className="space-y-16">
            {teamMembers.map((member, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                {/* Photo and Contact */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="bg-white rounded-2xl shadow-lg p-8">
                    <div className="text-center mb-6">
                      <div className="w-32 h-32 bg-gradient-to-br from-brand-primary-mid to-brand-text-light rounded-full mx-auto mb-4 flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">
                          {member.name.charAt(0)}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{member.name}</h3>
                      <p className="text-brand-primary-mid font-semibold">{member.role}</p>
                      <div className="flex items-center justify-center text-gray-600 mt-2">
                        <Award className="mr-2" size={16} />
                        <span className="text-sm">{member.experience}</span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center">
                        <Mail className="text-brand-primary-mid mr-3" size={18} />
                        <a 
                          href={`mailto:${member.contact.email}`}
                          className="text-gray-700 hover:text-brand-primary-mid transition-colors"
                        >
                          {member.contact.email}
                        </a>
                      </div>
                      <div className="flex items-center">
                        <Phone className="text-brand-primary-mid mr-3" size={18} />
                        <a 
                          href={`tel:${member.contact.phone}`}
                          className="text-gray-700 hover:text-brand-primary-mid transition-colors"
                        >
                          {member.contact.phone}
                        </a>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-3">{t('team.specialties')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {member.specialties.map((specialty, idx) => (
                          <span 
                            key={idx}
                            className="bg-brand-primary-mid/10 text-brand-primary-mid px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description and Expertise */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-4">
                        {member.name === 'Arnaud' ? t('team.expertFrance') : t('team.expertInternational')}
                      </h4>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {member.description}
                      </p>
                    </div>

                    <div>
                      <h5 className="text-lg font-semibold text-gray-900 mb-4">
                        {t('team.expertiseAreas')}
                      </h5>
                      <div className="space-y-3">
                        {member.expertise.map((skill, idx) => (
                          <div key={idx} className="flex items-start">
                            <div className="w-2 h-2 bg-brand-primary-mid rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-600">{skill}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link 
                        to="/rendez-vous"
                        className="bg-brand-primary-mid text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center"
                      >
                        <Calendar className="mr-2" size={18} />
                        {t('team.makeAppointment')}
                      </Link>
                      <a 
                        href={`mailto:${member.contact.email}`}
                        className="border-2 border-brand-primary-mid text-brand-primary-mid px-6 py-3 rounded-lg font-semibold hover:bg-brand-primary-mid hover:text-white transition-colors inline-flex items-center justify-center"
                      >
                        <Mail className="mr-2" size={18} />
                        {t('team.contactBtn')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('team.approach.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('team.approach.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-primary-mid rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('team.approach.teamwork.title')}
              </h3>
              <p className="text-gray-600">
                {t('team.approach.teamwork.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-primary-mid rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('team.approach.globalCoverage.title')}
              </h3>
              <p className="text-gray-600">
                {t('team.approach.globalCoverage.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-brand-primary-mid rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {t('team.approach.excellence.title')}
              </h3>
              <p className="text-gray-600">
                {t('team.approach.excellence.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-text-light text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('team.ctaTitle')}
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            {t('team.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/rendez-vous" 
              className="bg-brand-primary-mid text-white px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center"
            >
              <Calendar className="mr-2" size={20} />
              {t('common.makeAppointment')}
            </Link>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-brand-text-light transition-colors inline-flex items-center justify-center"
            >
              {t('common.contactUs')}
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;